import { useContext, useState } from 'react';
import { login, loginWithAppleCode, loginWithGoogleCode, logout } from '@/utils/auth';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useGoogleLogin } from '@react-oauth/google';
import { Google } from '@mui/icons-material';
import { SportsAppAppleSigninButton } from '@/screens/login/SportsAppAppleSigninButton';
import mainLogo from '@/images/login/logo-alpha.png';
import getItOnPlayStore from '@/images/play_store_badge.png';
import getItOnAppStore from '@/images/app_store_badge.svg';
import patreon from '@/images/patreon_badge.png';
import { AppContext } from '@/contexts/app.context';
import discordLogo from '@/images/Discord-Logo-White.png';
import { useParams } from 'react-router-dom';
import { LoginUser } from '@/models/user';
import { UserDTO } from '@/generated';

const addToLocalStorageCredentialListIfNotExists = (user: LoginUser) => {
  const array = getLocalStorageCredentialList();
  if (!array.find((u) => u.email === user.email && user.password === u.password)) array.push(user);
  localStorage.setItem('credential-list', JSON.stringify(array));
};

export const getLocalStorageCredentialList = () => {
  const list = localStorage.getItem('credential-list');
  let array = new Array<LoginUser>();
  if (list !== null) array = JSON.parse(list) as Array<LoginUser>;
  return array;
};

// export const deleteLocalUser = (user: LoginUser) => {
//   const array = getLocalStorageCredentialList().filter(u => u.username === user.username );
//   array.push(user);
//   localStorage.setItem('credential-list', JSON.stringify(array));
// };

export function Login(props: any) {
  const [email, setEmail] = useState<string>();
  const [openEmailLoginBox, setOpenEmailLoginBox] = useState<boolean>(false);
  const [password, setPassword] = useState<string>();
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [showUseTheAppModal, setShowUseTheAppModal] = useState(false);
  const [loginError, setLoginError] = useState<string>();
  const context = useContext(AppContext);
  const { id } = useParams();

  const loginWithRedirect = async (usernameOrEmail: string, password: string, redirectLocation: string) => {
    setLoginError(undefined);
    setShowBackdrop(true);
    try {
      const loginSucceeded = await login({ email: usernameOrEmail ?? '', password: password ?? '' });
      if (!loginSucceeded) {
        setLoginError('Login failed. Check your email address or username and password, then try again.');
        return;
      }

      const profile = await context.refreshUserProfile();
      addToLocalStorageCredentialListIfNotExists({ password, email: usernameOrEmail });
      checkOnboardingAndRedirect(profile);
    } finally {
      setShowBackdrop(false);
    }
  };

  const googleLoginWithRedirect = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      setShowBackdrop(true);
      await loginWithGoogleCode(codeResponse.code);
      const profile = await context.refreshUserProfile();
      checkOnboardingAndRedirect(profile);
      setShowBackdrop(false);
    },
    onError: () => {
      setShowBackdrop(false);
    },
    flow: 'auth-code',
  });

  const loginWithAppleRedirect = async (response: any) => {
    setShowBackdrop(true);
    await loginWithAppleCode(response.authorization.code);
    const profile = await context.refreshUserProfile();
    checkOnboardingAndRedirect(profile);
    setShowBackdrop(false);
  };

  const checkOnboardingAndRedirect = async (user?: UserDTO) => {
    if (user && !user.username) {
      console.log('No username configured. Showing "Use the App Modal"');
      setShowUseTheAppModal(true);
    } else if (user) {
      setShowUseTheAppModal(false);
      window.location.href = props.redirectLocation;
    }
  };

  return (
    <Container sx={{}} maxWidth="sm">
      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={showBackdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Stack sx={{ alignContent: 'center', alignItems: 'center', color: 'white', mb: 5, mx: 2 }} gap={2}>
        <img style={{ width: '80vw', height: 'auto', maxWidth: 400 }} src={mainLogo} />
        <Stack gap={1} sx={{ mt: 1 }} alignItems={'center'} component="form" noValidate autoComplete="off">
          {openEmailLoginBox && (
            <Stack sx={{ width: '100%' }} gap={2} alignItems={'center'}>
              {loginError && (
                <Alert severity="error" sx={{ width: '100%' }} onClose={() => setLoginError(undefined)}>
                  {loginError}
                </Alert>
              )}
              <TextField
                id="filled-basic"
                fullWidth
                value={email}
                label="Email address or username"
                onChange={(e: any) => {
                  setEmail(e.target.value);
                  setLoginError(undefined);
                }}
              />
              <TextField
                fullWidth
                type="password"
                label="Password"
                onChange={(e: any) => {
                  setPassword(e.target.value);
                  setLoginError(undefined);
                }}
              />
              <Button variant="outlined" fullWidth style={{ color: 'white' }} onClick={() => loginWithRedirect(email || '', password || '', props.redirectLocation)}>
                Login
              </Button>
              <Box style={{ color: 'white' }}>
                <a
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => {
                    setShowForgotPasswordModal(true);
                  }}
                >
                  Forgout your Password?
                </a>
              </Box>
            </Stack>
          )}
          {!openEmailLoginBox && (
            <Button variant="outlined" fullWidth style={{ color: 'white' }} onClick={() => setOpenEmailLoginBox(true)}>
              Login with Email/Password
            </Button>
          )}
          <Button variant="outlined" startIcon={<Google />} onClick={googleLoginWithRedirect} fullWidth style={{ color: 'white' }}>
            Sign in with Google
          </Button>
          <SportsAppAppleSigninButton onSuccess={loginWithAppleRedirect} />
        </Stack>
        <Typography sx={{ mt: 1 }}>
          Build custom exercise decks and train your way. Create decks for machines, free weights, calisthenics, stretching, mobility, yoga, and more — or add your own exercises
          and routines whenever you want. Generate workouts and full training plans, track your progress, and set individual timers for every exercise. You don’t need a rigid
          training plan. You need a system that keeps you consistent, motivated, and having fun—so training becomes something you look forward to, not something you skip.
        </Typography>

        <Stack sx={{ my: 2, gap: 3, justifyContent: 'center', width: '100%' }} alignSelf={'flex-start'} gap={2} direction={'row'}>
          <Link href="https://apps.apple.com/at/app/witness-the-fitness/id6740452975">
            <img style={{ height: 40, width: 120 }} loading="lazy" src={getItOnAppStore}></img>
          </Link>
          <Link href="https://play.google.com/store/apps/details?id=com.ss.checkcardsworkout.app&hl=de_AT">
            <img style={{ height: 40, width: 120 }} loading="lazy" src={getItOnPlayStore}></img>
          </Link>
        </Stack>
        <Stack sx={{ my: 2, gap: 3, justifyContent: 'center', width: '100%' }} alignSelf={'flex-start'} gap={2} direction={'row'}>
          <Link href="https://www.patreon.com/c/caliapp">
            <img style={{ height: 28 }} loading="lazy" src={patreon}></img>
          </Link>
          <Link sx={{ alignContent: 'center' }} href="https://discord.gg/E4UNr2qr">
            <img style={{ width: 150 }} loading="lazy" src={discordLogo}></img>
          </Link>
        </Stack>
        {/* 
        <Stack sx={{ width: '100%', mb: 5 }} gap={2}>
          <Link href="https://witness-the-fitness.com/user-guide">
            Check out the USER GUIDE: Find videos, tutorials and more! (The User guide is not optimized for mobile devices yet!)
          </Link>
        </Stack> */}
      </Stack>

      <Dialog open={showUseTheAppModal} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'Login with the App first'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Login with the Android or IOS App to Set-Up your Username and configure your initial set. You can do that here too, but the Apps provide a better user-experience.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUseTheAppModal(false);
              window.location.href = props.redirectLocation;
            }}
          >
            Nah, I know what I am doing
          </Button>
          <Button
            onClick={() => {
              logout();
              setShowUseTheAppModal(false);
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      <ForgotPasswordModal show={showForgotPasswordModal} setShow={setShowForgotPasswordModal}></ForgotPasswordModal>
    </Container>
  );
}
